import { useMemo, useEffect, useRef } from "react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { usePathname } from "next/navigation";
import { useStockMarketSchedule } from "./useStockMarketSchedule";
import Translations from "@translate/hooks/useMarketStatus";
import { StockMarketStateEnum } from "@/src/features/api/openData";
import {
  type StockType,
  getStockTypeFromPathname,
  DEFAULT_STOCK_TYPE,
} from "@/src/utils/stockType";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 获取市场状态文本映射
 * @returns 返回市场状态文本映射对象
 */
const getMarketStatusTextMap = (): Record<
  StockMarketStateEnum | "LUNCH",
  string
> => {
  return {
    [StockMarketStateEnum.PRE_MARKET]: I18n.t("Pre-market"),
    [StockMarketStateEnum.REGULAR_MARKET]: I18n.t("Regular Market"),
    [StockMarketStateEnum.POST_MARKET]: I18n.t("Post-market"),
    [StockMarketStateEnum.CLOSED_MARKET]: I18n.t("Market Closed"),
    [StockMarketStateEnum.AUCTION]: I18n.t("Market Closed"), // 不展示AUCTION状态
    [StockMarketStateEnum.NIGHT_MARKET]: I18n.t("Night Market"),
    LUNCH: I18n.t("Lunch"),
  };
};

/**
 * 获取市场对应的时区
 */
const getMarketTimeZone = (stockType: StockType): string => {
  return stockType === "US" ? "America/New_York" : "Asia/Hong_Kong";
};

/**
 * 替换字符串中的星期名称为多语言版本
 * @param text 包含星期名称的文本
 * @returns 替换后的文本
 */
const replaceWeekDays = (text: string): string => {
  const weekDaysMap: Record<string, string> = {
    Monday: I18n.t("Monday"),
    Tuesday: I18n.t("Tuesday"),
    Wednesday: I18n.t("Wednesday"),
    Thursday: I18n.t("Thursday"),
    Friday: I18n.t("Friday"),
    Saturday: I18n.t("Saturday"),
    Sunday: I18n.t("Sunday"),
  };

  let result = text;
  Object.keys(weekDaysMap).forEach((day) => {
    result = result.replace(new RegExp(day, "g"), weekDaysMap[day]);
  });

  return result;
};

/**
 * 从 phases 对象中提取所有未来的时间节点
 */
const extractFutureTimePoints = (
  phases:
    | Record<
        string,
        {
          start?: number;
          end?: number;
        }[]
      >
    | undefined,
  now: number,
): number[] => {
  if (!phases) {
    return [];
  }

  const timePoints: number[] = [];

  Object.values(phases).forEach((phaseArray) => {
    if (Array.isArray(phaseArray)) {
      phaseArray.forEach((phase) => {
        if (phase.start && phase.start > now) {
          timePoints.push(phase.start);
        }
        if (phase.end && phase.end > now) {
          timePoints.push(phase.end);
        }
      });
    }
  });

  return timePoints;
};

interface UseMarketStatusOptions {
  /** 股票市场类型（US | HK）
   * @note 优先使用传入的值，如果没有传入则从路由 pathname 解析
   */
  stockType?: StockType;
}

/**
 * Hook: 获取首页市场状态和时间
 * @param options.stockType 股票市场类型，优先使用传入的值
 */
export const useMarketStatus = (options?: UseMarketStatusOptions) => {
  const pathname = usePathname();
  // 优先使用传入的 stockType，fallback 到从 pathname 解析，最后使用默认值
  const stockType: StockType =
    options?.stockType ??
    getStockTypeFromPathname(pathname) ??
    DEFAULT_STOCK_TYPE;
  const { data: marketScheduleData, refetch } = useStockMarketSchedule();

  // 根据 stockType 获取对应的市场数据
  const marketData = useMemo(() => {
    if (!marketScheduleData) {
      return null;
    }
    return stockType === "US" ? marketScheduleData.us : marketScheduleData.hk;
  }, [marketScheduleData, stockType]);

  // 获取当前市场状态
  const marketState = useMemo(() => {
    return marketData?.current_state || StockMarketStateEnum.CLOSED_MARKET;
  }, [marketData]);

  // 获取时区
  const timeZone = useMemo(() => {
    return getMarketTimeZone(stockType);
  }, [stockType]);

  // 格式化时间（只显示日期，不需要实时更新）
  const formattedTime = useMemo(() => {
    const formatted = dayjs(Date.now())
      .tz(timeZone)
      // .format("ddd MMM DD HH:mm:ss [GMT]Z");
      .format("dddd MM/DD/YYYY");
    return replaceWeekDays(formatted);
  }, [timeZone]);

  // 获取状态文本 key
  const statusText = useMemo(() => {
    const statusTextMap = getMarketStatusTextMap();

    // 特殊处理：当 marketState 为 CLOSED_MARKET 时，检查是否是 LUNCH 状态
    if (marketState === StockMarketStateEnum.CLOSED_MARKET) {
      const regularMarketPhases =
        marketData?.schedule?.current?.phases?.REGULAR_MARKET;

      // 检查 REGULAR_MARKET 数组是否有两个对象
      if (
        Array.isArray(regularMarketPhases) &&
        regularMarketPhases.length === 2 &&
        regularMarketPhases[0]?.start &&
        regularMarketPhases[0]?.end &&
        regularMarketPhases[1]?.start &&
        regularMarketPhases[1]?.end
      ) {
        // 根据 start 时间排序，找出更早和较晚的时间段
        const sortedPhases = [...regularMarketPhases].sort(
          (a, b) => (a.start || 0) - (b.start || 0),
        );

        const earlierPhase = sortedPhases[0];
        const laterPhase = sortedPhases[1];

        // 使用更早时间段的 end 和较晚时间段的 start
        if (earlierPhase.end && laterPhase.start) {
          const now = Date.now();

          // 如果当前时间在两个阶段之间，返回 LUNCH 状态
          if (now >= earlierPhase.end && now < laterPhase.start) {
            return statusTextMap.LUNCH;
          }
        }
      }
    }

    return (
      statusTextMap[marketState] ||
      statusTextMap[StockMarketStateEnum.CLOSED_MARKET]
    );
  }, [marketState, marketData]);

  // 提取并排序所有未来的时间节点（从 current 和 next 中）
  const futureTimePoints = useMemo(() => {
    if (!marketData?.schedule) {
      return [];
    }

    const now = Date.now();
    const timePoints: number[] = [];

    // 从 current 和 next 中提取时间节点
    timePoints.push(
      ...extractFutureTimePoints(marketData.schedule.current?.phases, now),
      ...extractFutureTimePoints(marketData.schedule.next?.phases, now),
    );

    // 去重并排序
    return Array.from(new Set(timePoints)).sort((a, b) => a - b);
  }, [marketData]);

  // 使用 ref 存储当前设置的时间点，避免重复设置定时器
  const scheduledTimePointRef = useRef<number | null>(null);

  // 根据时间节点设置定时刷新（只设置第一个定时器，触发后会自动设置下一个）
  useEffect(() => {
    if (futureTimePoints.length === 0) {
      scheduledTimePointRef.current = null;
      return;
    }

    const now = Date.now();
    // 只取第一个未来的时间节点
    const nextTimePoint = futureTimePoints[0];

    // 如果已经为这个时间点设置了定时器，则不需要重新设置
    if (scheduledTimePointRef.current === nextTimePoint) {
      return;
    }

    const delay = nextTimePoint - now;

    if (delay <= 0) {
      scheduledTimePointRef.current = null;
      return;
    }

    // 记录当前设置的时间点
    scheduledTimePointRef.current = nextTimePoint;

    const timer = setTimeout(() => {
      // 刷新数据，数据更新后 futureTimePoints 会重新计算，useEffect 会重新执行设置下一个定时器
      scheduledTimePointRef.current = null; // 清除记录，允许设置新的定时器
      refetch();
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [futureTimePoints, refetch]);

  // 当 stockType 切换时，刷新市场数据（只在 stockType 变化时调用，避免循环）
  useEffect(() => {
    if (marketScheduleData) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockType]);

  return {
    marketState,
    formattedTime,
    statusText,
    timeZone,
    isLoading: !marketData,
  };
};
